/**
 * GlobalReportManager.js
 * Full-screen overlay for superadmin to create, preview, and assign
 * "Global Reports" — report templates rendered per-tenant with each
 * merchant's own data.
 *
 * Usage in SuperadminPanel:
 *   import { GlobalReportManager } from './GlobalReportManager';
 *   <GlobalReportManager tenants={tenants} onClose={() => setShowGlobal(false)} />
 */

import React, { useState, useEffect } from 'react';
import {
  getGlobalReports,
  saveGlobalReport,
  deleteGlobalReport,
  assignGlobalReport,
  getSharableUsers,
  shareGlobalReport,
  generateReport as apiGenerateReport,
} from '../services/api';
const ReactECharts = require('echarts-for-react').default;

// ── Dark theme (mirrors TenantBuilderTab) ──────────────────────────────────────
const D = {
  bg:        '#0f1117',
  surface:   '#1a1d27',
  elevated:  '#22253a',
  border:    'rgba(255,255,255,0.08)',
  accent:    '#a8e063',
  accentDim: 'rgba(168,224,99,0.12)',
  blue:      '#60a5fa',
  purple:    '#a78bfa',
  amber:     '#fbb724',
  danger:    '#ff6b6b',
  text:      '#e2e8f0',
  textSec:   '#94a3b8',
  textMuted: '#64748b',
};

const dBtn = (variant = 'primary') => ({
  padding: '7px 14px', borderRadius: '8px', border: 'none',
  fontSize: '12px', fontWeight: '600', cursor: 'pointer',
  background:
    variant === 'primary'  ? D.accent :
    variant === 'danger'   ? 'rgba(255,107,107,0.15)' :
    variant === 'blue'     ? 'rgba(96,165,250,0.15)'  :
    variant === 'purple'   ? 'rgba(167,139,250,0.15)' :
    D.elevated,
  color:
    variant === 'primary'  ? '#000'    :
    variant === 'danger'   ? D.danger  :
    variant === 'blue'     ? D.blue    :
    variant === 'purple'   ? D.purple  :
    D.textSec,
});

const optBtn = (active, color = D.accent) => ({
  padding: '5px 11px', border: `1px solid ${active ? color : D.border}`,
  borderRadius: '20px', fontSize: '12px', cursor: 'pointer', fontWeight: '500',
  background: active ? color : D.elevated,
  color: active ? '#000' : D.textSec,
});

const sectionLabel = { fontSize: '11px', fontWeight: '700', color: D.textMuted, margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.5px' };

// ── Generic dimensions only — staff & customer excluded ────────────────────────
const GLOBAL_DIMS = [
  { value: 'payment_mode',   label: 'Payment Mode'     },
  { value: 'product',        label: 'Product / Service' },
  { value: 'booking_status', label: 'Booking Status'   },
  { value: 'classify_a',     label: 'Classify A'        },
  { value: 'classify_b',     label: 'Classify B'        },
  { value: 'age_group',      label: 'Age Group'         },
  { value: 'date',           label: 'Date'              },
];

const MEASURES = [
  { value: 'revenue',          label: 'Revenue'          },
  { value: 'transactions',     label: 'Transactions'     },
  { value: 'unique_customers', label: 'Unique Customers' },
  { value: 'avg_value',        label: 'Avg Revenue'      },
  { value: 'commission',       label: 'Commission'       },
  { value: 'bookings',         label: 'Bookings'         },
];

const CHART_TYPES = [
  { value: 'bar',   label: 'Bar',   icon: '📊' },
  { value: 'line',  label: 'Line',  icon: '📈' },
  { value: 'table', label: 'Table', icon: '🗃️' },
];

const DATE_PERIODS = [
  { value: 'daily',   label: 'Daily'   },
  { value: 'weekly',  label: 'Weekly'  },
  { value: 'monthly', label: 'Monthly' },
];

const CHART_COLORS = [
  '#2563eb','#16a34a','#d97706','#dc2626','#7c3aed',
  '#0891b2','#ea580c','#4f46e5','#059669','#b45309',
];

// Generic color-by and filter dims — staff & customer excluded
const GLOBAL_COLOR_BY_DIMS = [
  { value: 'payment_mode',   label: 'Payment Mode'     },
  { value: 'product',        label: 'Product'           },
  { value: 'booking_status', label: 'Booking Status'   },
  { value: 'classify_a',     label: 'Classify A'        },
  { value: 'classify_b',     label: 'Classify B'        },
  { value: 'age_group',      label: 'Age Group'         },
];

const GLOBAL_FILTER_DIMS = [
  { value: 'payment_mode',   label: 'Payment Mode'     },
  { value: 'product',        label: 'Product / Service' },
  { value: 'booking_status', label: 'Booking Status'   },
  { value: 'classify_a',     label: 'Classify A'        },
  { value: 'classify_b',     label: 'Classify B'        },
  { value: 'age_group',      label: 'Age Group'         },
];

const uid = () => Math.random().toString(36).slice(2, 8);

const fmtVal = (v) => {
  const n = Number(v || 0);
  return n >= 1000 ? n.toLocaleString(undefined, { maximumFractionDigits: 0 }) : String(parseFloat(n.toFixed(2)));
};

// ── Assign-to-Tenants modal ────────────────────────────────────────────────────
const AssignModal = ({ report, tenants, onClose, onSaved }) => {
  const [selected, setSelected] = useState(report.assigned_tenants || []);
  const [saving, setSaving]     = useState(false);

  const toggle = (tid) => setSelected(s => s.includes(tid) ? s.filter(x => x !== tid) : [...s, tid]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await assignGlobalReport(report.id, selected);
      onSaved(report.id, selected);
      onClose();
    } catch { /* silent */ } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
         onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: D.surface, border: `1px solid ${D.border}`, borderRadius: '16px', padding: '24px', width: '460px', maxHeight: '75vh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.6)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
          <p style={{ fontSize: '15px', fontWeight: '700', color: D.text, margin: 0 }}>Assign "{report.report_name}"</p>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: D.textMuted }}>✕</button>
        </div>
        <p style={{ fontSize: '12px', color: D.textMuted, margin: '0 0 16px' }}>
          Select which merchants receive this report. Each merchant's boss will see it rendered with their own data.
        </p>

        {/* Select All / None shortcuts */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          <button onClick={() => setSelected(tenants.map(t => t.tenant_id))} style={{ ...dBtn('blue'), fontSize: '11px', padding: '4px 10px' }}>All</button>
          <button onClick={() => setSelected([])} style={{ ...dBtn('secondary'), fontSize: '11px', padding: '4px 10px' }}>None</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '20px' }}>
          {tenants.map(t => {
            const active = selected.includes(t.tenant_id);
            return (
              <div key={t.tenant_id} onClick={() => toggle(t.tenant_id)}
                style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderRadius: '8px', cursor: 'pointer',
                  background: active ? 'rgba(168,224,99,0.08)' : D.elevated,
                  border: `1px solid ${active ? D.accent : D.border}` }}>
                <div style={{ width: '16px', height: '16px', borderRadius: '4px', flexShrink: 0,
                  background: active ? D.accent : 'transparent', border: `2px solid ${active ? D.accent : D.textMuted}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {active && <span style={{ color: '#000', fontSize: '9px', fontWeight: '700' }}>✓</span>}
                </div>
                <div>
                  <p style={{ fontSize: '13px', fontWeight: '600', color: D.text, margin: 0 }}>{t.tenant_name}</p>
                  <p style={{ fontSize: '11px', color: D.textMuted, margin: 0, fontFamily: 'monospace' }}>{t.tenant_id}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={onClose} style={{ flex: 1, padding: '10px', background: D.elevated, border: `1px solid ${D.border}`, borderRadius: '8px', fontSize: '13px', cursor: 'pointer', color: D.textSec }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ flex: 1, padding: '10px', background: D.accent, border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', color: '#000', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving...' : `✓ Assign to ${selected.length} merchant${selected.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Left panel: global report list ────────────────────────────────────────────
const ReportList = ({ reports, loading, onLoad, onAssign, onDelete, deleting }) => (
  <div style={{ width: '300px', flexShrink: 0, borderRight: `1px solid ${D.border}`, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
    <p style={{ fontSize: '13px', fontWeight: '700', color: D.text, margin: '0 0 12px' }}>
      Global Reports <span style={{ color: D.textMuted, fontWeight: '400' }}>({reports.length})</span>
    </p>
    {loading && <p style={{ color: D.textMuted, fontSize: '12px' }}>Loading...</p>}
    {!loading && reports.length === 0 && (
      <p style={{ color: D.textMuted, fontSize: '12px', textAlign: 'center', paddingTop: '24px' }}>
        No global reports yet.<br />Create one using the builder →
      </p>
    )}
    {reports.map(r => (
      <div key={r.id} style={{ background: D.elevated, border: `1px solid ${D.border}`, borderRadius: '10px', padding: '10px 12px' }}>
        <div onClick={() => onLoad(r)} style={{ cursor: 'pointer', marginBottom: '8px' }}>
          <p style={{ fontSize: '13px', fontWeight: '600', color: D.text, margin: 0 }}>{r.report_name}</p>
          <p style={{ fontSize: '11px', color: D.textMuted, margin: '3px 0 0' }}>
            {r.x_field} · {r.y_field} · {r.chart_type}
          </p>
          <p style={{ fontSize: '11px', color: D.accent, margin: '4px 0 0' }}>
            {r.assigned_tenants?.length > 0
              ? `Assigned to ${r.assigned_tenants.length} merchant${r.assigned_tenants.length !== 1 ? 's' : ''}`
              : <span style={{ color: D.amber }}>Not assigned yet</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button onClick={() => onAssign(r)} style={{ ...dBtn('blue'), fontSize: '11px', padding: '3px 9px', flex: 1 }}>
            🏪 Assign
          </button>
          <button onClick={() => onDelete(r.id, r.report_name)} disabled={deleting === r.id}
            style={{ padding: '3px 9px', background: 'transparent', border: `1px solid ${D.danger}`, borderRadius: '6px', color: D.danger, fontSize: '11px', cursor: 'pointer', opacity: deleting === r.id ? 0.5 : 1 }}>
            {deleting === r.id ? '...' : '🗑'}
          </button>
        </div>
      </div>
    ))}
  </div>
);

// ── Main GlobalReportManager ───────────────────────────────────────────────────
export const GlobalReportManager = ({ tenants, onClose }) => {
  const [reports,   setReports]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [deleting,  setDeleting]  = useState(null);
  const [assignModal, setAssignModal] = useState(null); // report object
  const builderRef = React.useRef(null);

  const load = async () => {
    setLoading(true);
    try { const r = await getGlobalReports(); setReports(r.data.data || []); }
    catch { /* silent */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete global report "${name}"? This will remove it from all merchants.`)) return;
    setDeleting(id);
    try { await deleteGlobalReport(id); setReports(s => s.filter(r => r.id !== id)); }
    catch { /* silent */ } finally { setDeleting(null); }
  };

  const handleAssigned = (id, tenantIds) => {
    setReports(s => s.map(r => r.id === id ? { ...r, assigned_tenants: tenantIds } : r));
  };

  // Called when BuilderPanel saves a new report
  const handleSaved = async () => { await load(); };

  // Called when user clicks a report in list — load its config into builder
  const handleLoadReport = (r) => {
    if (builderRef.current?.loadConfig) builderRef.current.loadConfig(r);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: D.bg, zIndex: 300, display: 'flex', flexDirection: 'column', fontFamily: "'DM Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ background: D.surface, borderBottom: `1px solid ${D.border}`, padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '56px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '14px', fontWeight: '700', color: D.text }}>🌐 Global Reports</span>
          <div style={{ width: '1px', height: '20px', background: D.border }} />
          <span style={{ fontSize: '12px', color: D.textMuted }}>Create once · assign to all merchants · each sees their own data</span>
        </div>
        <button onClick={onClose}
          style={{ padding: '7px 16px', background: 'rgba(255,107,107,0.15)', border: `1px solid ${D.danger}`, borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', color: D.danger }}>
          ✕ Close
        </button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
        <ReportList
          reports={reports}
          loading={loading}
          onLoad={handleLoadReport}
          onAssign={r => setAssignModal(r)}
          onDelete={handleDelete}
          deleting={deleting}
        />
        <BuilderPanelWithRef ref={builderRef} tenants={tenants} onSaved={handleSaved} />
      </div>

      {assignModal && (
        <AssignModal
          report={assignModal}
          tenants={tenants}
          onClose={() => setAssignModal(null)}
          onSaved={handleAssigned}
        />
      )}
    </div>
  );
};

// Wrap BuilderPanel to expose loadConfig via ref
const BuilderPanelWithRef = React.forwardRef(({ tenants, onSaved }, ref) => {
  const [key, setKey]   = useState(0);
  const [init, setInit] = useState(null); // initial config to load

  // Expose loadConfig to parent via ref
  React.useImperativeHandle(ref, () => ({
    loadConfig: (r) => {
      setInit(r);
      setKey(k => k + 1); // remount builder with new initial values
    }
  }));

  return <BuilderPanelInner key={key} tenants={tenants} onSaved={onSaved} initialConfig={init} />;
});

// BuilderPanel with optional initialConfig prop
const BuilderPanelInner = ({ tenants, onSaved, initialConfig }) => {
  const fj = initialConfig?.filter_json || {};
  const [dimension,    setDimension]  = useState(initialConfig?.x_field      || 'payment_mode');
  const [measure,      setMeasure]    = useState(initialConfig?.y_field       || 'revenue');
  const [chartType,    setChartType]  = useState(initialConfig?.chart_type    || 'bar');
  const [datePeriod,   setDatePeriod] = useState(fj.date_period               || 'daily');
  const [dateFrom,     setDateFrom]   = useState(fj.date_from                 || '');
  const [dateTo,       setDateTo]     = useState(fj.date_to                   || '');
  const [colorBy,      setColorBy]    = useState(fj.color_by                  || '');
  const [extraFilters, setExtraFilters]= useState(
    (fj.filters || []).map(f => ({ id: uid(), dimension: f.dimension, values: f.values || '' }))
  );
  const [previewTenant,setPreviewT]   = useState(tenants[0]?.tenant_id        || '');
  const [reportData,   setReportData] = useState(null);
  const [loading,      setLoading]    = useState(false);
  const [saving,       setSaving]     = useState(false);
  const [reportName,   setReportName] = useState(initialConfig ? initialConfig.report_name + ' (copy)' : '');
  const [message,      setMessage]    = useState(initialConfig ? `Loaded "${initialConfig.report_name}"` : '');
  const [msgType,      setMsgType]    = useState('ok');

  // Reset colorBy if it becomes the same as dimension
  useEffect(() => { if (colorBy && colorBy === dimension) setColorBy(''); }, [dimension]);

  const takenDims           = new Set([dimension, ...(colorBy ? [colorBy] : []), ...extraFilters.map(f => f.dimension).filter(Boolean)]);
  const availableForColorBy = GLOBAL_COLOR_BY_DIMS.filter(d => d.value !== dimension && !extraFilters.some(f => f.dimension === d.value));
  const availableForFilter  = GLOBAL_FILTER_DIMS.filter(d => !takenDims.has(d.value));

  const addFilter        = () => { if (availableForFilter.length === 0) return; setExtraFilters(f => [...f, { id: uid(), dimension: '', values: '' }]); };
  const removeFilter     = (id) => setExtraFilters(f => f.filter(x => x.id !== id));
  const updateFilterDim  = (id, dim) => setExtraFilters(f => f.map(x => x.id === id ? { ...x, dimension: dim, values: '' } : x));
  const updateFilterVals = (id, vals) => setExtraFilters(f => f.map(x => x.id === id ? { ...x, values: vals } : x));

  const showMsg = (text, type = 'ok') => { setMessage(text); setMsgType(type); };

  const generate = async () => {
    if (!previewTenant) { showMsg('Select a preview merchant first.', 'err'); return; }
    setLoading(true); setReportData(null); setMessage('');
    try {
      const params = { dimension, measure, sa_tenant: previewTenant };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo)   params.date_to   = dateTo;
      if (dimension === 'date') params.period = datePeriod;
      if (colorBy) params.color_by = colorBy;
      extraFilters.forEach(f => {
        if (f.dimension && f.values.trim()) params[`ef_${f.dimension}`] = f.values.trim();
      });
      const res = await apiGenerateReport(params);
      setReportData(res.data);
    } catch (err) {
      showMsg(err.response?.data?.error || 'Failed to generate.', 'err');
    } finally { setLoading(false); }
  };

  const save = async () => {
    if (!reportName.trim()) { showMsg('Enter a report name.', 'err'); return; }
    setSaving(true);
    try {
      await saveGlobalReport({
        report_name: reportName,
        x_field:     dimension,
        y_field:     measure,
        chart_type:  chartType,
        filter_json: {
          date_from:   dateFrom,
          date_to:     dateTo,
          date_period: datePeriod,
          color_by:    colorBy,
          filters:     extraFilters.filter(f => f.dimension).map(f => ({ dimension: f.dimension, values: f.values })),
        },
      });
      showMsg(`"${reportName}" saved. Now click Assign on the left to push it to merchants.`, 'ok');
      setReportName('');
      onSaved && onSaved();
    } catch (err) {
      showMsg(err.response?.data?.error || 'Failed to save.', 'err');
    } finally { setSaving(false); }
  };

  const getChartOpt = () => {
    if (!reportData) return {};
    const mLabel = MEASURES.find(m => m.value === measure)?.label || measure;
    if (reportData.type === 'series') {
      const labels = (reportData.data || []).map(d => String(d.label));
      const series = reportData.series || [];
      return {
        backgroundColor: 'transparent', tooltip: { trigger: 'axis' },
        legend: { data: series, top: 0, type: 'scroll', textStyle: { fontSize: 10, color: D.textSec } },
        grid: { left: 8, right: 8, top: 44, bottom: 20, containLabel: true },
        xAxis: { type: 'category', data: labels, axisLabel: { rotate: labels.length > 6 ? 30 : 0, fontSize: 10, color: D.textMuted } },
        yAxis: { type: 'value', axisLabel: { fontSize: 10, color: D.textMuted, formatter: v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v } },
        series: series.map((name, i) => ({ name, type: chartType === 'line' ? 'line' : 'bar', smooth: chartType === 'line', data: (reportData.data || []).map(d => d[name] || 0), itemStyle: { color: CHART_COLORS[i % CHART_COLORS.length] } })),
      };
    }
    const labels = (reportData.data || []).map(d => String(d.label));
    const values = (reportData.data || []).map(d => parseFloat(d.value || 0));
    return {
      backgroundColor: 'transparent', tooltip: { trigger: 'axis' },
      grid: { left: 8, right: 8, top: 8, bottom: 20, containLabel: true },
      xAxis: { type: 'category', data: labels, axisLabel: { rotate: labels.length > 6 ? 30 : 0, fontSize: 10, color: D.textMuted } },
      yAxis: { type: 'value', name: mLabel, axisLabel: { fontSize: 10, color: D.textMuted, formatter: v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v } },
      series: [{ type: chartType === 'line' ? 'line' : 'bar', smooth: chartType === 'line', data: values, itemStyle: { color: D.accent } }],
    };
  };

  const renderTable = () => {
    if (!reportData) return null;
    const mLabel = MEASURES.find(m => m.value === measure)?.label || measure;
    if (reportData.type === 'series') {
      const series = reportData.series || [];
      return (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead><tr>
              <th style={{ padding: '6px 10px', textAlign: 'left', color: D.textMuted, borderBottom: `1px solid ${D.border}`, background: D.elevated }}>Label</th>
              {series.map(n => <th key={n} style={{ padding: '6px 10px', textAlign: 'right', color: D.textMuted, borderBottom: `1px solid ${D.border}`, background: D.elevated }}>{n}</th>)}
              <th style={{ padding: '6px 10px', textAlign: 'right', color: D.textMuted, borderBottom: `1px solid ${D.border}`, background: D.elevated }}>Total</th>
            </tr></thead>
            <tbody>{(reportData.data || []).map((row, i) => { const total = series.reduce((s, n) => s + (row[n] || 0), 0); return <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : D.elevated }}><td style={{ padding: '6px 10px', color: D.text }}>{String(row.label)}</td>{series.map(n => <td key={n} style={{ padding: '6px 10px', textAlign: 'right', color: D.textSec }}>{fmtVal(row[n])}</td>)}<td style={{ padding: '6px 10px', textAlign: 'right', color: D.text, fontWeight: '700' }}>{fmtVal(total)}</td></tr>; })}</tbody>
          </table>
        </div>
      );
    }
    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead><tr>
            <th style={{ padding: '6px 10px', textAlign: 'left', color: D.textMuted, borderBottom: `1px solid ${D.border}`, background: D.elevated }}>Label</th>
            <th style={{ padding: '6px 10px', textAlign: 'right', color: D.textMuted, borderBottom: `1px solid ${D.border}`, background: D.elevated }}>{mLabel}</th>
          </tr></thead>
          <tbody>{(reportData.data || []).map((row, i) => <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : D.elevated }}><td style={{ padding: '6px 10px', color: D.text }}>{String(row.label)}</td><td style={{ padding: '6px 10px', textAlign: 'right', color: D.textSec }}>{fmtVal(row.value)}</td></tr>)}</tbody>
        </table>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

      {/* Config */}
      <div style={{ width: '320px', flexShrink: 0, borderRight: `1px solid ${D.border}`, overflowY: 'auto', padding: '20px' }}>
        <div style={{ background: 'rgba(251,183,36,0.08)', border: `1px solid ${D.amber}`, borderRadius: '8px', padding: '10px 12px', marginBottom: '16px' }}>
          <p style={{ fontSize: '11px', color: D.amber, margin: 0 }}>
            <strong>Generic dimensions only.</strong> Staff and Customer are excluded since their values differ per merchant.
          </p>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <p style={sectionLabel}>Dimension (X-axis)</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {GLOBAL_DIMS.map(d => <button key={d.value} onClick={() => setDimension(d.value)} style={optBtn(dimension === d.value)}>{d.label}</button>)}
          </div>
        </div>

        {dimension === 'date' && (
          <div style={{ marginBottom: '16px' }}>
            <p style={sectionLabel}>Period</p>
            <div style={{ display: 'flex', gap: '6px' }}>
              {DATE_PERIODS.map(p => <button key={p.value} onClick={() => setDatePeriod(p.value)} style={optBtn(datePeriod === p.value, D.blue)}>{p.label}</button>)}
            </div>
          </div>
        )}

        <div style={{ marginBottom: '16px' }}>
          <p style={sectionLabel}>Measure (Y-axis)</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {MEASURES.map(m => <button key={m.value} onClick={() => setMeasure(m.value)} style={optBtn(measure === m.value, D.blue)}>{m.label}</button>)}
          </div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <p style={sectionLabel}>Chart Type</p>
          <div style={{ display: 'flex', gap: '8px' }}>
            {CHART_TYPES.map(c => (
              <button key={c.value} onClick={() => setChartType(c.value)}
                style={{ flex: 1, padding: '10px 8px', border: `2px solid ${chartType === c.value ? D.accent : D.border}`, borderRadius: '8px', background: chartType === c.value ? D.accentDim : D.elevated, cursor: 'pointer', color: chartType === c.value ? D.accent : D.textSec, fontSize: '12px', fontWeight: chartType === c.value ? '700' : '400', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                <span style={{ fontSize: '18px' }}>{c.icon}</span>{c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Color By */}
        <div style={{ marginBottom: '16px' }}>
          <p style={sectionLabel}>Color By <span style={{ fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}>(optional)</span></p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {availableForColorBy.map(d => (
              <button key={d.value} onClick={() => setColorBy(colorBy === d.value ? '' : d.value)}
                style={optBtn(colorBy === d.value, D.purple)}>{d.label}</button>
            ))}
            {colorBy && (
              <button onClick={() => setColorBy('')} style={{ ...dBtn('secondary'), fontSize: '11px', padding: '3px 9px' }}>✕ Clear</button>
            )}
          </div>
          {availableForColorBy.length === 0 && (
            <p style={{ fontSize: '11px', color: D.textMuted, margin: '4px 0 0' }}>No more dimensions available.</p>
          )}
        </div>

        {/* Filters */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <p style={{ ...sectionLabel, margin: 0 }}>Filters <span style={{ fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}>(optional)</span></p>
            <button onClick={addFilter} disabled={availableForFilter.length === 0}
              style={{ ...dBtn('blue'), fontSize: '11px', padding: '3px 10px', opacity: availableForFilter.length === 0 ? 0.4 : 1 }}>
              + Add
            </button>
          </div>
          {extraFilters.length === 0
            ? <p style={{ fontSize: '11px', color: D.textMuted }}>No filters.</p>
            : extraFilters.map(f => (
              <div key={f.id} style={{ marginBottom: '8px', background: D.elevated, borderRadius: '8px', padding: '8px 10px', border: `1px solid ${D.border}` }}>
                <div style={{ display: 'flex', gap: '6px', marginBottom: '6px', alignItems: 'center' }}>
                  <select value={f.dimension} onChange={e => updateFilterDim(f.id, e.target.value)}
                    style={{ flex: 1, padding: '5px 8px', background: D.surface, border: `1px solid ${D.border}`, borderRadius: '6px', fontSize: '11px', color: D.text, outline: 'none' }}>
                    <option value="">— dimension —</option>
                    {GLOBAL_FILTER_DIMS.filter(d => d.value === f.dimension || !takenDims.has(d.value)).map(d => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                  <button onClick={() => removeFilter(f.id)}
                    style={{ padding: '4px 8px', background: 'transparent', border: `1px solid ${D.danger}`, borderRadius: '6px', color: D.danger, fontSize: '11px', cursor: 'pointer' }}>✕</button>
                </div>
                {f.dimension && (
                  <input
                    value={f.values}
                    onChange={e => updateFilterVals(f.id, e.target.value)}
                    placeholder="e.g. Cash,Card,Online"
                    style={{ width: '100%', padding: '5px 8px', background: D.surface, border: `1px solid ${D.border}`, borderRadius: '6px', fontSize: '11px', color: D.text, outline: 'none', boxSizing: 'border-box' }}
                  />
                )}
                {f.dimension && (
                  <p style={{ fontSize: '10px', color: D.textMuted, margin: '4px 0 0' }}>Comma-separated values. Leave empty to include all.</p>
                )}
              </div>
            ))
          }
        </div>

        <div style={{ marginBottom: '16px' }}>
          <p style={sectionLabel}>Date Range (optional)</p>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: '10px', color: D.textMuted, margin: '0 0 4px' }}>From</p>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                style={{ width: '100%', padding: '7px 10px', background: D.elevated, border: `1px solid ${D.border}`, borderRadius: '6px', fontSize: '12px', color: D.text, outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: '10px', color: D.textMuted, margin: '0 0 4px' }}>To</p>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                style={{ width: '100%', padding: '7px 10px', background: D.elevated, border: `1px solid ${D.border}`, borderRadius: '6px', fontSize: '12px', color: D.text, outline: 'none', boxSizing: 'border-box' }} />
            </div>
          </div>
          {(dateFrom || dateTo) && (
            <button onClick={() => { setDateFrom(''); setDateTo(''); }} style={{ ...dBtn('secondary'), fontSize: '11px', padding: '4px 10px' }}>Clear dates</button>
          )}
        </div>

        <div style={{ marginBottom: '20px' }}>
          <p style={sectionLabel}>Preview using merchant</p>
          <select value={previewTenant} onChange={e => setPreviewT(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', background: D.elevated, border: `1px solid ${D.border}`, borderRadius: '8px', fontSize: '12px', color: D.text, outline: 'none' }}>
            <option value="">— pick a merchant —</option>
            {tenants.map(t => <option key={t.tenant_id} value={t.tenant_id}>{t.tenant_name} ({t.tenant_id})</option>)}
          </select>
        </div>

        <button onClick={generate} disabled={loading || !previewTenant}
          style={{ width: '100%', padding: '12px', background: loading || !previewTenant ? D.elevated : D.accent, color: loading || !previewTenant ? D.textMuted : '#000', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: loading || !previewTenant ? 'not-allowed' : 'pointer', marginBottom: '12px' }}>
          {loading ? 'Generating...' : '▶ Generate Preview'}
        </button>

        <div style={{ display: 'flex', gap: '8px' }}>
          <input value={reportName} onChange={e => setReportName(e.target.value)}
            placeholder="Report name to save..."
            style={{ flex: 1, padding: '9px 12px', background: D.elevated, border: `1px solid ${D.border}`, borderRadius: '8px', fontSize: '12px', color: D.text, outline: 'none' }} />
          <button onClick={save} disabled={saving} style={{ ...dBtn('primary'), opacity: saving ? 0.7 : 1 }}>
            {saving ? '...' : '💾 Save'}
          </button>
        </div>
        {message && (
          <p style={{ fontSize: '12px', marginTop: '8px', color: msgType === 'ok' ? D.accent : D.danger }}>{message}</p>
        )}
      </div>

      {/* Preview */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
        {!reportData && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '300px', color: D.textMuted, gap: '12px' }}>
            <p style={{ fontSize: '40px', margin: 0 }}>📊</p>
            <p style={{ fontSize: '13px' }}>Configure and generate a preview above.</p>
          </div>
        )}
        {reportData && (
          <div style={{ background: D.surface, border: `1px solid ${D.border}`, borderRadius: '12px', overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: `1px solid ${D.border}` }}>
              <p style={{ fontSize: '14px', fontWeight: '700', color: D.text, margin: 0 }}>
                {GLOBAL_DIMS.find(d => d.value === dimension)?.label || dimension} by {MEASURES.find(m => m.value === measure)?.label || measure}
              </p>
              <p style={{ fontSize: '12px', color: D.textMuted, margin: '2px 0 0' }}>
                {reportData.data?.length || 0} data points · previewing as {previewTenant}
              </p>
            </div>
            <div style={{ padding: '16px' }}>
              {chartType !== 'table'
                ? <ReactECharts option={getChartOpt()} style={{ height: '320px' }} notMerge />
                : renderTable()
              }
            </div>
          </div>
        )}
      </div>
    </div>
  );
};